/**
 * The BOARD facet's READING types — the client mirror of `BoardViewDTO`.
 *
 * <p>Separate from `boardTypes.ts` (authoring) on purpose. That module is reachable from the eager
 * startup path because `CreateDiscussion` needs the empty draft and the publish mapping; this one is
 * reached only from the lazily-imported `BoardGrid`, and nothing eager may import it. Keeping the two
 * apart is also what keeps the authoring schema and the rendered board from drifting into one
 * ambiguous shape that half-describes both.
 *
 * <p><b>There is no per-slot tally in this file, and none may be added.</b> Design §5.2 hides the
 * distribution until the viewer has voted, and this payload is server-rendered into HTML that every
 * anonymous reader and every crawler receives. The distribution arrives only in the vote RESPONSE, to
 * the account that just earned it — see `BoardVoteResult`.
 *
 * <p>Design: designs/01-now/2026-08-06-post-facets-board-facet-collaborative-tier-lists-and-alignment-charts-design.md §4.1, §5.2, §11.1.
 */

import type { BoardKind } from "@/main/react/board/boardTypes";
import {
    getUriForImage,
    getUserOwnedImageBasePath,
    SMALL_THUMBNAIL_IMAGE_SIZE,
} from "@/main/util/ImageUrlHelper";

/** One row of a TIERS board, or one cell of a MATRIX one. Mirrors `BoardSlotViewDTO`. */
export type BoardSlotView = {
    key: string;
    label: string;
    /** A `--board-tier-N` token NAME, never a hex. Null on MATRIX, whose cells use a neutral surface. */
    colorToken?: string | null;
    description?: string | null;
};

export type BoardItemState = "QUEUED" | "OPEN" | "SETTLED";

/**
 * Mirrors `BoardSettleMethod.java`. `AUTO_QUORUM` (2026-08-24) is the vote deciding it on its own —
 * no owner, no clock. Widening this union obliges the same widening in `chk_board_item_settle_method`,
 * and the database has to go first.
 */
export type BoardSettleMethod = "VOTE_LEADER" | "OWNER_OVERRIDE" | "AUTO_TIMEOUT" | "AUTO_QUORUM";

/** Mirrors `BoardItemViewDTO`. `votesCount` is the TOTAL — never a per-slot breakdown. */
export type BoardItemView = {
    id: number;
    label: string;
    imagePublicId?: string | null;
    state: BoardItemState;
    openedAt?: string | null;
    settledSlotKey?: string | null;
    settledAt?: string | null;
    settleMethod?: BoardSettleMethod | null;
    overrideReason?: string | null;
    votesCount: number;
    thoughtsCount: number;
    /**
     * Where THIS viewer placed this item, or null — design 2026-08-24 §8 (D4).
     *
     * <p>Null for an anonymous reader, null on the feed card by design, and null for an item they
     * never voted on. It is the viewer's OWN row and nobody else's: the query behind it is gated
     * `WHERE v.user_id = :viewerId`, and this payload is server-rendered into HTML every anonymous
     * reader and every crawler receives, so a field that could carry a second person's vote would put
     * the distribution §5.2 hides onto a public page.
     *
     * <p>Distinct from `BoardView.viewerVoteSlotKey`, which answers the same question about the OPEN
     * item and drives the vote control's pressed state. Both exist because they are different
     * questions: "what am I voting" and "what did I already decide".
     */
    viewerSlotKey?: string | null;
};

/**
 * Mirrors `BoardCardSummaryDTO` — the feed card's slice of a board (design §18.2).
 *
 * <p>A strict subset of `BoardView`, and it must stay strict: the card renders the rows, the open item
 * and the viewer's own placement, and everything else is payload multiplied by the cards on a page.
 *
 * <p><b>No per-slot tally here either.</b> The same §5.2 rule this file states for `BoardView` applies
 * unchanged, and it matters more: this payload is served to anonymous readers on a public feed.
 */
export type BoardCardSummary = {
    kind: BoardKind;
    slots: BoardSlotView[];
    openItem?: BoardItemView | null;
    viewerVoteSlotKey?: string | null;
    /**
     * The placements the card draws into its rows, capped server-side at `MAX_CARD_PLACED_ITEMS` across
     * the WHOLE card rather than per row — a card is a preview, and the overflow reads as `+N` inside the
     * row it belongs to (2026-08-13 redesign §5).
     *
     * <p>These rows carry `settledSlotKey`, `label` and `imagePublicId` and deliberately NOT the settle
     * method, the override reason or the thought count: that is the record, and the record is the post.
     */
    placedItems?: BoardItemView[] | null;
    /** The head of the queue, so the card's tray is not empty on a board nobody has played yet. */
    upcomingItems?: BoardItemView[] | null;
    itemsTotal: number;
    itemsSettled: number;
    complete: boolean;
    /**
     * The tier-label column's width in px at the CARD's scale (§6.1, V1).
     *
     * <p>A different number from `BoardView.labelColumnPx` for the same board, because the card draws
     * the labels at `text-xs`. Optional on the wire so a frontend that reaches an environment ahead of
     * its backend falls back to the old fixed width rather than collapsing the column to zero.
     */
    labelColumnPx?: number | null;
    /** The author's per-board voting window, so the card can say when the open item closes (§7). */
    itemWindowHours?: number | null;
};

/** Mirrors `BoardViewDTO`. */
export type BoardView = {
    kind: BoardKind;
    schemaJson: string;
    /**
     * @deprecated §18.1 (2026-08-08) — no longer authored or rendered. Still on the wire for boards
     * published before the change; do not build anything new on either.
     */
    criterion?: string | null;
    /** @deprecated §18.1 — see `criterion`. */
    rules?: string | null;
    slots: BoardSlotView[];
    openItem?: BoardItemView | null;
    settledItems: BoardItemView[];
    upcomingItems: BoardItemView[];
    queuedRemaining: number;
    itemsTotal: number;
    itemsSettled: number;
    complete: boolean;
    itemWindowHours: number;
    /** Whether to DRAW the settle control. Every settle verb re-derives ownership on the server. */
    viewerIsOwner: boolean;
    /** The viewer's own placement on the open item, or null. The §5.2 gate — never anyone else's. */
    viewerVoteSlotKey?: string | null;
    /**
     * The tier-label column's width in px, computed server-side from THIS board's own labels (§6.1).
     *
     * <p><b>One number, shipped rather than derived, so the SSR grid and the island cannot disagree
     * about the geometry of the same board</b> — rule 32's requirement, and the reason this is not two
     * implementations of one formula. `BoardLabelColumn` on the server holds the rule and the argument
     * for why a CSS `minmax(3rem, max-content)` track is the wrong tool.
     */
    labelColumnPx?: number | null;
};

/** One slot's standing, returned ONLY by the vote response. Never present in the SSR payload. */
export type BoardSlotTally = {
    slotKey: string;
    votes: number;
};

export type BoardVoteResult = {
    voted: boolean;
    /**
     * True when the item settled before this tap landed, so nothing was recorded.
     *
     * <p><b>Not an error.</b> Since 2026-08-24 three people agreeing settles an item, so arriving a
     * moment late is an ordinary event rather than a rare one — the server answers 200 carrying the
     * NEW board, and the surface says the board moved on rather than showing a failure line. See
     * `AjaxBoardRestController.vote` and `ValidationErrorCodes.BOARD_VOTE_CLOSED`.
     */
    movedOn?: boolean;
    slotKey: string | null;
    tally: BoardSlotTally[];
    /**
     * Whether THIS vote settled the open item and advanced the queue.
     *
     * <p>Distinct from `movedOn`, which is the opposite case: somebody else's vote got there first.
     */
    settled?: boolean;
    /**
     * The whole board, as the post page renders it, AFTER whatever this vote did.
     *
     * <p><b>The response carries the board because a settle is now the common outcome of a vote</b>
     * (design 2026-08-24 §5). A settle the reader cannot see did not happen, and before this the
     * response carried a tally and nothing else — so even a settle that DID occur server-side stayed
     * invisible until a reload.
     *
     * <p>Optional on the wire so a frontend that reaches an environment ahead of its backend degrades
     * to the optimistic local patch instead of blanking the count.
     */
    board?: BoardView | null;
    /**
     * The same board in the feed card's shape.
     *
     * <p>Two fields rather than one, deliberately: the card renders `BoardCardSummary`, whose placed
     * rows are capped and stripped server-side, and deriving that from `BoardView` on the client would
     * put a second implementation of `BoardViewService.capAndStrip` in TypeScript — the exact drift
     * `BoardCardSummaryDTO` exists to prevent. Both are assembled from ONE row read.
     */
    cardSummary?: BoardCardSummary | null;
};

export type BoardSettleResult = {
    result: "SETTLED" | "SKIPPED" | "UNDER_QUORUM" | "SCHEMA_DRIFT" | "LOST_RACE";
    itemId: number | null;
    slotKey: string | null;
    settleMethod: BoardSettleMethod | null;
    nextOpenItemId: number | null;
    boardComplete: boolean;
    /**
     * The board after the settle — added 2026-08-24 so the owner's control can PATCH instead of
     * calling `window.location.reload()`.
     *
     * <p>The reload was honest when it was written: a settle advances the queue, stamps the board and
     * may complete it, which was more than `BoardManage` could patch coherently into a board it does
     * not own. Sending the board removes the reason rather than the symptom.
     */
    board?: BoardView | null;
    /** The same board in the card's shape. See `BoardVoteResult.cardSummary` for why both. */
    cardSummary?: BoardCardSummary | null;
};

/**
 * Reads the board the server inlined beside the grid it rendered.
 *
 * <p><b>Must run in `getProps`, i.e. BEFORE `mountReactNode`</b> — the payload lives inside the mount
 * container and `createRoot()` discards the container's children. Identical constraint, and identical
 * reasoning, to `parseInlinedThoughts` in `DiscussionPage`.
 *
 * <p>Every failure mode returns `undefined`, which means "fetch instead". An absent element (a degraded
 * server render, or the deferred shell), unparseable JSON, or a payload without slots must never become
 * an empty board — that would replace the grid the server rendered with nothing, which is the one
 * outcome rule 32 exists to prevent.
 */
export function parseInlinedBoardView(element: HTMLElement): BoardView | undefined {
    const payloadEl = element.querySelector("script[data-board-view]");
    if (!payloadEl) {
        return undefined;
    }
    try {
        const parsed = JSON.parse(payloadEl.textContent ?? "");
        if (!parsed || typeof parsed !== "object" || !Array.isArray(parsed.slots)) {
            return undefined;
        }
        return parsed as BoardView;
    } catch {
        return undefined;
    }
}

/**
 * The tier ramp's CSS custom property for a slot, or the neutral surface.
 *
 * <p>Returns a `var(--board-tier-N)` reference rather than a colour, so the value stays defined in one
 * place for both themes (§4.1b). A slot whose token is missing or malformed falls back to the neutral
 * surface instead of to a guessed hue — colour is decorative here, and a wrong colour on a row whose
 * label and position already carry the meaning is a cosmetic problem, where a hardcoded one is a
 * theme bug.
 */
export function slotBackground(slot: BoardSlotView): string {
    if (!slot.colorToken || !/^tier-[1-8]$/.test(slot.colorToken)) {
        return "var(--background2)";
    }
    return `var(--board-${slot.colorToken})`;
}

/**
 * The label column's width for a board, with the pre-§6.1 fallbacks.
 *
 * <p>The fallbacks are the fixed widths this replaces — `w-14` (56px) on the page, `w-9` (36px) on the
 * card — so a payload without the field renders exactly as it did before rather than collapsing the
 * column. That is rule 32's shape applied to a number: never LESS than what already worked.
 */
export function labelColumnWidth(px: number | null | undefined, fallback: number): number {
    return typeof px === "number" && px > 0 ? px : fallback;
}

/** The pre-§6.1 fixed widths, kept as the named fallbacks above rather than as magic numbers. */
export const PAGE_LABEL_COLUMN_FALLBACK_PX = 56;
export const CARD_LABEL_COLUMN_FALLBACK_PX = 36;

/**
 * §8's mark for one item, or null — the client twin of `BoardItemViewDTO.viewerAgreed/viewerDisagreed`.
 *
 * <p>Returns null for the three cases that must render nothing at all: the viewer did not vote, the
 * item is not settled, or they are anonymous. Kept here rather than in the tile so the SSR predicate
 * and this one are one rule stated twice in the same words, not two rules that happen to agree.
 */
export function viewerMarkFor(item: BoardItemView): "agreed" | "disagreed" | null {
    if (item.state !== "SETTLED" || !item.viewerSlotKey) {
        return null;
    }
    return item.viewerSlotKey === item.settledSlotKey ? "agreed" : "disagreed";
}

/** The items placed in one slot, in placement order. The client twin of `BoardViewDTO.itemsInSlot`. */
export function itemsInSlot(board: BoardView, slotKey: string): BoardItemView[] {
    return board.settledItems.filter((item) => item.settledSlotKey === slotKey);
}

/**
 * The same question asked of any list of placements — the feed card's rows come from
 * `BoardCardSummary.placedItems`, which is a capped subset rather than the whole record.
 */
export function placementsInSlot(items: readonly BoardItemView[] | null | undefined, slotKey: string) {
    return (items ?? []).filter((item) => item.settledSlotKey === slotKey);
}

/**
 * A board item's square thumbnail, or null when the item is text.
 *
 * <p>Built from `getUserOwnedImageBasePath` plus the small-thumbnail size segment rather than by hand:
 * `board_item.image_public_id` stores a `user_images.public_id`, never a URL (master design §10.1 — a
 * URL field would re-create the redistribution problem the first-party-upload posture avoids), and the
 * `/image/media/{id}` → `/st` shape is owned by `ImageUrlHelper` for every other surface in the app.
 */
export function boardItemThumbnailUrl(imagePublicId?: string | null): string | null {
    const publicId = imagePublicId?.trim();
    if (!publicId) {
        return null;
    }
    return getUriForImage(getUserOwnedImageBasePath(publicId), SMALL_THUMBNAIL_IMAGE_SIZE);
}
